import React, { ComponentType, useContext, useState } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FormikHelpers as FormikActions } from "formik"
import { Box, Grid, DialogContent, styled } from "@mui/material"

import i18n from "config/i18n"
import {
  withModalStep,
  withModalDebt,
  withModalSubscriptionPlan,
  withPlans,
  withPaymentScheduleDates,
} from "hocs/features"
import { useAppDispatch } from "redux/hooks"
import { SubscriptionContext } from "redux/contexts/subscription"
import PollingService from "redux/api/pollingService"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import {
  closeDialog,
  updateDialogStep,
  updateDialog,
} from "redux/features/dialog/dialogSlice"
import { makePaymentSchedule } from "redux/features/payment_schedule/paymentScheduleThunk"
import { calculateSliderValues, calculatePlan } from "helpers/payment_schedule"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import Spinner from "components/ui/Spinner"
import { Headline } from "components/PaymentSchedule/elements"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import Title from "components/PaymentSchedule/Title"
import Description from "components/PaymentSchedule/Description"
import Form from "components/PaymentSchedule/Create/Form"
import Error from "components/PaymentSchedule/Create/Error"
import Help from "components/PaymentSchedule/Help"
import Manager from "components/PaymentSchedule/Manager"
import Plans from "components/Subscription/Plans/List"
import {
  PAYMENT_SCHEDULE_PROMISE_ACTION,
  PAYMENT_SCHEDULE_SIGN_ACTION,
  PS_CREATE_STEP_INITIAL,
  PS_CREATE_STEP_CHOOSE_SUBSCRIPTION,
  PS_CREATE_STEP_CHOOSE_PAYMENT_METHOD,
  PS_CREATE_STEP_CHOOSE_START_DATE,
  PS_CREATE_STEP_FAILURE,
  SUBSCRIPTION_CREATE_MATCH,
} from "config"
import {
  DebtType,
  PaymentScheduleDate,
  PaymentScheduleFormValues,
  PaymentScheduleType,
  PlanListType,
  PlanOptionType,
} from "types"

type Props = {
  debt: DebtType
  payment_schedule: PaymentScheduleType
  plans: PlanListType
  dates: PaymentScheduleDate[]
  step: number
}

const PaymentScheduleCreateComponent = ({
  debt,
  dates,
  plans,
  step,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { needSubscription } = useContext(SubscriptionContext)

  const isBackHidden = [PS_CREATE_STEP_INITIAL].includes(step)
  const isSubscription = [
    PS_CREATE_STEP_CHOOSE_SUBSCRIPTION,
    PS_CREATE_STEP_CHOOSE_PAYMENT_METHOD,
  ].includes(step)
  const isTitle = !isSubscription

  const [isSubmitting, setSubmitting] = useState(false)

  const isDates = dates.length > 0

  const { reference_number, balance, is_white, manager } = debt

  const { amountOptions, periodOptions } = calculateSliderValues(balance)
  const [chosenPlan, setChosenPlan] = useState(
    calculatePlan(periodOptions.middle),
  )

  const action = is_white
    ? PAYMENT_SCHEDULE_PROMISE_ACTION
    : PAYMENT_SCHEDULE_SIGN_ACTION

  const handleContinue = () => {
    dispatch(updateDialogStep({ step: PS_CREATE_STEP_CHOOSE_SUBSCRIPTION }))
  }

  const handleChange = (period: number) => {
    const newValue = calculatePlan(period)
    setChosenPlan(newValue)
  }

  const handleChoosePlan = (plan: PlanOptionType) => {
    dispatch(
      updateDialog({
        content: {
          plan: plan,
        },
      }),
    )

    navigate(SUBSCRIPTION_CREATE_MATCH)
  }

  const handleSubmit = (
    values: PaymentScheduleFormValues,
    actions: FormikActions<object>,
  ) => {
    setSubmitting(true)

    const { amount, period, start_date } = values

    dispatch(
      makePaymentSchedule({
        debt_id: debt.id,
        payments_per_month: amount,
        number_of_payments: period,
        start_date,
      }),
    )
      .then(({ payment_schedule_id }) => {
        dispatch(
          showNotice({
            message: t("notice.payment_schedule.create.success"),
          }),
        )

        navigate(
          `/debts/${debt.id}/payment_schedules/${payment_schedule_id}/${action}`,
        )
      })
      .catch(() => {
        dispatch(updateDialogStep({ step: PS_CREATE_STEP_FAILURE }))
      })
      .finally(() => {
        setSubmitting(false)
        actions.setSubmitting(false)
      })
  }

  const handleBack = () => {
    switch (step) {
      case PS_CREATE_STEP_CHOOSE_SUBSCRIPTION:
        dispatch(updateDialogStep({ step: PS_CREATE_STEP_INITIAL }))
        return
      case PS_CREATE_STEP_CHOOSE_PAYMENT_METHOD:
        dispatch(updateDialogStep({ step: PS_CREATE_STEP_CHOOSE_SUBSCRIPTION }))
        return
      case PS_CREATE_STEP_FAILURE:
        dispatch(updateDialogStep({ step: PS_CREATE_STEP_INITIAL }))
        return
      default:
        return
    }
  }

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
    navigate("/")
  }

  const STEPS = needSubscription
    ? [
        {
          label: PS_CREATE_STEP_INITIAL,
          component: (
            <Content>
              <Description />
              {isDates && (
                <Form
                  isChoose
                  initialValues={{
                    amount: amountOptions.middle,
                    period: periodOptions.middle,
                    start_date: dates[0].value,
                  }}
                  options={{
                    amountOptions: amountOptions,
                    periodOptions: periodOptions,
                  }}
                  dates={dates}
                  chosenPlan={chosenPlan}
                  onChoose={handleContinue}
                  onChange={handleChange}
                  onSubmit={handleContinue}
                />
              )}
            </Content>
          ),
        },
        {
          label: PS_CREATE_STEP_CHOOSE_SUBSCRIPTION,
          component: (
            <Content>
              <Plans
                items={plans}
                chosen={chosenPlan}
                onChoose={handleChoosePlan}
              />
            </Content>
          ),
        },
        {
          label: PS_CREATE_STEP_CHOOSE_START_DATE,
          component: (
            <Content>
              <Description />
              {isDates && (
                <Form
                  isChoose={false}
                  initialValues={{
                    amount: amountOptions.middle,
                    period: periodOptions.middle,
                    start_date: dates[0].value,
                  }}
                  options={{
                    amountOptions: amountOptions,
                    periodOptions: periodOptions,
                  }}
                  dates={dates}
                  chosenPlan={chosenPlan}
                  onChoose={handleContinue}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                />
              )}
            </Content>
          ),
        },
        {
          label: PS_CREATE_STEP_FAILURE,
          component: (
            <Content>
              <Error debt={debt} />
            </Content>
          ),
        },
      ]
    : [
        {
          label: PS_CREATE_STEP_CHOOSE_START_DATE,
          component: (
            <Content>
              <Description />
              {isDates && (
                <Form
                  isChoose={false}
                  initialValues={{
                    amount: amountOptions.middle,
                    period: periodOptions.middle,
                    start_date: dates[0].value,
                  }}
                  options={{
                    amountOptions: amountOptions,
                    periodOptions: periodOptions,
                  }}
                  dates={dates}
                  chosenPlan={chosenPlan}
                  onChoose={handleContinue}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                />
              )}
            </Content>
          ),
        },
        {
          label: 1,
          component: <></>,
        },
        {
          label: 2,
          component: <></>,
        },
        {
          label: PS_CREATE_STEP_FAILURE,
          component: (
            <Content>
              <Error debt={debt} />
            </Content>
          ),
        },
      ]

  return (
    <DialogContent>
      <Spinner open={isSubmitting} />
      <Back
        disabled={isSubmitting}
        hidden={isBackHidden}
        onClick={handleBack}
      />
      <Close onClick={handleClose} />
      <Container
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Headline>
          {isTitle && (
            <Title reference_number={reference_number}>
              {t("features.payment_schedule.title")}
            </Title>
          )}
        </Headline>

        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index: number) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>

        <Help balance={balance} />
        {manager && (
          <Manager manager={manager}>
            {isSubscription
              ? t("features.subscription.manager.description")
              : t("features.payment_schedule.manager.description")}
          </Manager>
        )}
      </Container>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalDebt,
  withModalSubscriptionPlan,
  withPlans,
  withPaymentScheduleDates,
)(PaymentScheduleCreateComponent)

export const Container = styled(Grid)(() => ({
  marginBottom: "0px",
}))

const Content = styled(Box)(() => ({
  margin: "0 0 24px 0",
  padding: "0",
}))
